import * as THREE from 'three';
import { Pass, FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

// ── FXOverlayPass ────────────────────────────────────────────────────────────
//
// Re-draws the transparent FX — particle sprites and smoke-trail ribbons — on top
// of the already-composited cloud deck.
//
// The problem it solves is the one CockpitOverlayPass solves for the canopy, in
// its cheaper form: the clouds are a screen-space pass that clips its raymarch
// against the scene depth buffer, and transparent materials don't write depth.
// Both FX systems are `depthWrite: false` (they must be — alpha-blended and
// unsorted), so at every FX pixel the depth buffer still holds whatever is
// *behind* it. The deck therefore composites straight over smoke, dust,
// explosions and missile trails whenever it sits between them and the camera.
//
// Getting the FX to interleave *correctly* with the deck would mean testing each
// sprite and ribbon segment against the cloud volume's own depth/transmittance — a
// read of a buffer the raymarch doesn't currently produce. This pass takes the
// cheap way out instead: the FX always win. App parks their meshes on
// FX_OVERLAY_LAYER (so the main RenderPass, and therefore the cloud pass, skips
// them) and this pass redraws them afterwards. It runs before bloom, so emissive
// sparks, flares and rocket plumes keep the glow they had in the beauty pass.
//
// ── Where the depth comes from ───────────────────────────────────────────────
//
// Unlike the cockpit overlay, this pass must NOT clear depth: terrain, the
// airframe and enemies all have to keep occluding the FX. It doesn't have to
// restore any either. The composer ping-pongs two render targets; RenderPass
// draws the scene into `readBuffer` with `needsSwap = false`, so that target owns
// this frame's depth. CloudLayer then reads it, composites into `writeBuffer` and
// swaps — which hands *this* pass a `writeBuffer` that is the RenderPass's own
// target, scene depth still intact. So we blit the cloud-composited colour back
// over it (colour only; the quad neither tests nor writes depth) and draw the FX
// layer with an ordinary depth test.
//
// That invariant holds only while the cloud pass runs immediately before this one
// and swaps. App inserts it there and enables it exactly when CloudLayer is
// enabled — with no deck up there is nothing to draw over, so the FX stay on
// layer 0 and render inside the beauty pass as they always did.

// Render layer the transparent FX are parked on while this pass is active. Must
// not collide with App's COCKPIT_OVERLAY_LAYER (1) / COCKPIT_OCCLUDER_LAYER (3)
// or the Maverick seeker's HOT_LAYER (2).
export const FX_OVERLAY_LAYER = 4;

const COPY_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const COPY_FRAG = /* glsl */`
  uniform sampler2D tDiffuse;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(tDiffuse, vUv); }
`;

export default class FXOverlayPass extends Pass {
  /**
   * @param {{ scene: THREE.Scene, camera: THREE.PerspectiveCamera, layer?: number }} opts
   */
  constructor({ scene, camera, layer = FX_OVERLAY_LAYER }) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.layer = layer;
    this.needsSwap = true;

    // Off until App sees a cloud deck; a disabled pass is skipped entirely by the
    // composer, so a scenario with clear skies pays nothing for this.
    this.enabled = false;

    this._copyMat = new THREE.ShaderMaterial({
      name: 'FXOverlayCopy',
      uniforms: { tDiffuse: { value: null } },
      vertexShader: COPY_VERT,
      fragmentShader: COPY_FRAG,
      depthTest: false,
      depthWrite: false,
    });
    this._fsq = new FullScreenQuad(this._copyMat);
  }

  render(renderer, writeBuffer, readBuffer) {
    // Never the last pass (OutputPass always follows), so `writeBuffer` — and the
    // scene depth riding in it — is always what we get.
    const target = this.renderToScreen ? null : writeBuffer;
    const prevAutoClear = renderer.autoClear;
    const prevShadowAuto = renderer.shadowMap.autoUpdate;
    const prevBackground = this.scene.background;
    const camMask = this.camera.layers.mask;

    // 1) Blit the cloud-composited image over the pre-cloud beauty pass still
    //    sitting in the target. autoClear stays off and the quad is depth-inert,
    //    so the scene depth already attached to this target survives untouched.
    renderer.autoClear = false;
    this._copyMat.uniforms.tDiffuse.value = readBuffer.texture;
    renderer.setRenderTarget(target);
    this._fsq.render(renderer);

    // 2) Draw the FX layer against that depth. The scene background has to be
    //    suppressed — the renderer would repaint the HDR sky over the blit — and
    //    the shadow map frozen, since the main RenderPass already built it this
    //    frame and nothing on this layer casts.
    renderer.shadowMap.autoUpdate = false;
    this.scene.background = null;
    this.camera.layers.set(this.layer);
    renderer.render(this.scene, this.camera);

    this.camera.layers.mask = camMask;
    this.scene.background = prevBackground;
    renderer.shadowMap.autoUpdate = prevShadowAuto;
    renderer.autoClear = prevAutoClear;
  }

  dispose() {
    this._copyMat.dispose();
    this._fsq.dispose();
  }
}
